import { addYears, formatISO } from 'date-fns';

import { getEnv } from '../utilities/env';

import deMessages from '../../translations/de.json';
import enMessages from '../../translations/en.json';
import esMessages from '../../translations/es.json';
import frMessages from '../../translations/fr.json';
import itMessages from '../../translations/it.json';

import {
    AvailableLocale,
    IntlJsonMessages,
    IntlState,
    ReactIntlMessages,
    ReactIntlMessagesObj,
} from './types';

export const availableLocales: { [key in AvailableLocale]: AvailableLocale } = {
    fr: 'fr',
    en: 'en',
    it: 'it',
    de: 'de',
    es: 'es',
};

const localesMap: {
    [key: string]: AvailableLocale;
} = availableLocales;

export const checkSupportedLocales = (locale: string): AvailableLocale | void => {
    if (localesMap[locale]) {
        return localesMap[locale];
    }
    return undefined;
};

function flattenMessages(nestedMessages: IntlJsonMessages, prefix = ''): IntlJsonMessages {
    return Object.keys(nestedMessages).reduce((messages, key: string) => {
        const value = nestedMessages[key];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            return { ...messages, [prefixedKey]: value };
        }

        return { ...messages, ...flattenMessages(value, prefixedKey) };
    }, {});
}

const locales: ReactIntlMessagesObj = {
    de: {
        messages: deMessages as unknown as IntlJsonMessages,
        code: 'de-DE',
    },
    en: {
        messages: enMessages as unknown as IntlJsonMessages,
        code: 'en-GB',
    },
    es: {
        messages: esMessages as unknown as IntlJsonMessages,
        code: 'es-ES',
    },
    fr: {
        messages: frMessages as unknown as IntlJsonMessages,
        code: 'fr-FR',
    },
    it: {
        messages: itMessages as unknown as IntlJsonMessages,
        code: 'it-IT',
    },
};

export const getTranslations = (locale: IntlState): ReactIntlMessages => {
    if (locale === null) {
        return {
            code: 'zu-ZU', // Zulu code is used for tests
            messages: {},
        };
    }
    return {
        code: locales[locale].code,
        messages: flattenMessages(locales[locale].messages),
    };
};

export const setLocaleInCookie = (locale: AvailableLocale): void => {
    const expirationDate = formatISO(addYears(new Date(), 10));
    document.cookie = `request-manager_locale=${locale};path=/; expires=${expirationDate}`;
};

export function getPROHostByLocale(locale: AvailableLocale): string {
    const proEnv = getEnv('PRO_ENV') || '';
    switch (locale) {
        case 'fr':
            return `pro.${proEnv}evaneos.com`;
        case 'en':
            return `pro.${proEnv}evaneos-travel.com`;
        case 'it':
            return `pro.${proEnv}evaneos.it`;
        case 'es':
            return `pro.${proEnv}evaneos.es`;
        case 'de':
            return `pro.${proEnv}evaneos.de`;
        default:
            return `pro.${proEnv}evaneos.com`;
    }
}

export function getEvaneosHostByLocale(locale: AvailableLocale): string {
    switch (locale) {
        case 'fr':
            return `www.evaneos.fr`;
        case 'en':
            return `www.evaneos.com`;
        case 'it':
            return `www.evaneos.it`;
        case 'es':
            return `www.evaneos.es`;
        case 'de':
            return `www.evaneos.de`;
        default:
            return `www.evaneos.com`;
    }
}

export function getLocaleCode(locale: AvailableLocale): string {
    if (locales[locale]) {
        return locales[locale].code;
    }

    return 'en-GB';
}
